/** Optional, task-wide priority. Smaller values come first; legacy entries omit it. */
export function isReviewPriority(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

/** Keep the existing order for ties and unranked images. Never mutate the caller's array. */
export function orderByReviewPriority<T extends { reviewPriority?: unknown }>(items: readonly T[]): T[] {
  return [...items].sort((left, right) =>
    (isReviewPriority(left.reviewPriority) ? left.reviewPriority : Infinity) -
    (isReviewPriority(right.reviewPriority) ? right.reviewPriority : Infinity)
  );
}

/** Firestore client and Admin timestamps use the same public seconds/nanoseconds fields. */
function exactTimestamp(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const { seconds, nanoseconds } = value as { seconds?: number; nanoseconds?: number };
  if (typeof seconds !== "number" || typeof nanoseconds !== "number" ||
      !Number.isSafeInteger(seconds) || !Number.isInteger(nanoseconds) || nanoseconds < 0 || nanoseconds >= 1e9) return undefined;
  const date = new Date(seconds * 1000);
  if (!Number.isFinite(date.getTime())) return undefined;
  return date.toISOString().replace(/\.\d{3}Z$/, `.${String(nanoseconds).padStart(9, "0")}Z`);
}

export interface RelabelReviewEvidence {
  version: 1;
  entryKey: string;
  imgCloudPath: string;
  captureMetadataCloudPath: string;
  status: "reviewed" | "unreviewed" | "unknown";
  priority?: number;
  reviewedAt?: string;
  reviewedBy?: string;
  updatedAt?: string;
  /** SHA-256 of the original parsed sidecar, before COCO filtering or normalization. */
  annotationMetadataSha256?: string;
}

/** JSON key order does not change the metadata identity. Array order and every value do. */
function canonicalJsonValue(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJsonValue).join(",")}]`;
  if (value !== null && typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) =>
      `${JSON.stringify(key)}:${canonicalJsonValue((value as Record<string, unknown>)[key])}`
    ).join(",")}}`;
  }
  return JSON.stringify(value);
}

export function canonicalReviewMetadata(value: unknown): string {
  // Match the JSON wire representation, including omitted optional object properties.
  return canonicalJsonValue(JSON.parse(JSON.stringify(value)));
}

export async function reviewMetadataSha256(metadata: unknown): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error("Review evidence export requires HTTPS or localhost. Open the secure app URL and try again.");
  }
  const bytes = new TextEncoder().encode(canonicalReviewMetadata(metadata));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return "sha256:" + Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
}

/** Read current sidecars only for reviewed images, with at most eight concurrent reads. */
export async function bindReviewedMetadata(
  progress: RelabelReviewProgress, readMetadata: (path: string) => Promise<unknown>
): Promise<void> {
  const reviewed = progress.images.filter((image) => image.status === "reviewed");
  for (let offset = 0; offset < reviewed.length; offset += 8) {
    await Promise.all(reviewed.slice(offset, offset + 8).map(async (image) => {
      image.annotationMetadataSha256 = await reviewMetadataSha256(await readMetadata(image.captureMetadataCloudPath));
    }));
  }
}

/** Stable JSON inventory contract. Counters include explicit reviews, not preview visits. */
export interface RelabelReviewProgress {
  kind: "gleanag-relabel-review-progress";
  version: 1;
  total: number;
  reviewed: number;
  unreviewed: number;
  unknown: number;
  ranked: number;
  priorityOrderValid: boolean;
  /** Number of reviewed ranked images before the first gap. */
  reviewedPrefixCount: number;
  reviewedThroughPriority: number | null;
  highestReviewedPriority: number | null;
  firstUnreviewedPriority: number | null;
  latestReviewedAt: string | null;
  latestReviewedBy: string | null;
  images: RelabelReviewEvidence[];
}

/** Seed annotation provenance and task state do not prove human review. */
export function relabelReviewEvidence(
  entry: Record<string, unknown>, collectionId: string, type: string, sampleKey: string
): RelabelReviewEvidence {
  const reviewedAt = exactTimestamp(entry.analysisReviewedAt);
  const updatedAt = exactTimestamp(entry.updatedAt);
  const reviewer = entry.analysisReviewedBy;
  const reviewerPath = typeof reviewer === "string" ? reviewer :
    (reviewer && typeof reviewer === "object" ? (reviewer as { path?: unknown }).path : undefined);
  const reviewedBy = typeof reviewerPath === "string" && /^users\/[^/]+$/.test(reviewerPath) ? reviewerPath : undefined;
  let status: RelabelReviewEvidence["status"] = "unknown";
  if (entry.analysisStatus === "completed" && entry.analysisReviewStatus === "reviewed" && reviewedAt && reviewedBy) {
    status = "reviewed";
  } else if (entry.analysisReviewStatus === "deferred_unreviewed") {
    // Reporting includes pending work. The UI separately requires completed analysis before review.
    status = "unreviewed";
  }
  return {
    version: 1, entryKey: JSON.stringify([collectionId, type, sampleKey]),
    imgCloudPath: typeof entry.imgCloudPath === "string" ? entry.imgCloudPath : "",
    captureMetadataCloudPath: typeof entry.captureMetadataCloudPath === "string" ? entry.captureMetadataCloudPath : "",
    status,
    ...(isReviewPriority(entry.reviewPriority) ? { priority: entry.reviewPriority } : {}),
    ...(reviewedAt ? { reviewedAt } : {}), ...(reviewedBy ? { reviewedBy } : {}),
    ...(updatedAt ? { updatedAt } : {}),
  };
}

/** Read-only, per-image progress for editable relabel entries, including empty-box negatives. */
export function buildRelabelReviewProgress(collections: readonly Record<string, unknown>[]): RelabelReviewProgress {
  const images: RelabelReviewEvidence[] = [];
  for (const collection of collections) {
    for (const type of ["imageMeasurement", "standCountMeasurement"]) {
      const entries = collection[`${type}Entries`];
      if (!entries || typeof entries !== "object") continue;
      for (const [sampleKey, entry] of Object.entries(entries)) {
        if (!entry || typeof entry !== "object" || !entry.imgCloudPath || !entry.captureMetadataCloudPath) continue;
        images.push(relabelReviewEvidence(entry, String(collection.id), type, sampleKey));
      }
    }
  }
  images.sort((a, b) => (a.priority ?? Infinity) - (b.priority ?? Infinity) || a.entryKey.localeCompare(b.entryKey));
  const ranked = images.filter((image) => image.priority !== undefined);
  const reviewed = images.filter((image) => image.status === "reviewed");
  let reviewedPrefixCount = 0;
  for (const image of ranked) {
    if (image.status !== "reviewed") break;
    reviewedPrefixCount += 1;
  }
  const reviewedPriorities = reviewed.flatMap((image) => image.priority === undefined ? [] : [image.priority]);
  const latest = [...reviewed].sort((a, b) => b.reviewedAt!.localeCompare(a.reviewedAt!))[0];
  return {
    kind: "gleanag-relabel-review-progress", version: 1,
    total: images.length, reviewed: reviewed.length,
    unreviewed: images.filter((image) => image.status === "unreviewed").length,
    unknown: images.filter((image) => image.status === "unknown").length,
    ranked: ranked.length, priorityOrderValid: new Set(ranked.map((image) => image.priority)).size === ranked.length,
    reviewedPrefixCount,
    reviewedThroughPriority: reviewedPrefixCount ? ranked[reviewedPrefixCount - 1].priority ?? null : null,
    highestReviewedPriority: reviewedPriorities.length ? Math.max(...reviewedPriorities) : null,
    firstUnreviewedPriority: ranked.find((image) => image.status !== "reviewed")?.priority ?? null,
    latestReviewedAt: latest?.reviewedAt ?? null, latestReviewedBy: latest?.reviewedBy ?? null,
    images,
  };
}
